import { useState, type ReactElement } from 'react';
import { History } from 'lucide-react';
import type { GroupedAlertSessionLink } from '@kirin/api-client/contracts/agent-sessions';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { useAlertSessionSummary } from '@/hooks/api/useAgentSessionTimeline';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { flagsmithFeatureFlags } from '@/lib/flagsmith';
import { SessionTimelineDrawer } from './SessionTimelineDrawer';
import { SESSION_TIMELINE_BUTTON_HINT } from './session-timeline-display';

export interface SessionTimelineButtonProps {
  /** The occurrence the session endpoints key on, and its session. */
  link: GroupedAlertSessionLink | undefined;
  /** Omitted / null → personal-account scope. */
  organizationId?: string | null;
}

/**
 * The alert panel's entry to the session timeline (KIR-4445), with the
 * session's event count on a second line. The session id and "Keep session
 * events" live in the drawer only. Renders nothing unless the flag is on and
 * the alert carries a session link.
 */
export function SessionTimelineButton({
  link,
  organizationId,
}: SessionTimelineButtonProps): ReactElement | null {
  const { flags } = useFeatureFlags([
    flagsmithFeatureFlags.AGENT_SESSION_TIMELINE,
  ]);
  const enabled = flags[flagsmithFeatureFlags.AGENT_SESSION_TIMELINE] === true;
  if (!enabled || !link) return null;
  return (
    <SessionTimelineButtonContent
      // A grouped alert's latest occurrence can change under this button; a
      // new session must not inherit the old one's open drawer.
      key={`${link.occurrenceId}:${link.sessionId}`}
      organizationId={organizationId ?? null}
      occurrenceId={link.occurrenceId}
      sessionId={link.sessionId}
    />
  );
}

function SessionTimelineButtonContent({
  organizationId,
  occurrenceId,
  sessionId,
}: {
  organizationId: string | null;
  occurrenceId: string;
  sessionId: string;
}): ReactElement {
  const summary = useAlertSessionSummary(organizationId, occurrenceId);
  const [open, setOpen] = useState(false);

  const count = summary.data
    ? `${summary.data.eventCount} event${summary.data.eventCount === 1 ? '' : 's'}`
    : summary.isLoading
      ? 'Loading…'
      : 'Events unavailable';

  return (
    <>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setOpen(true)}
            data-testid="session-timeline-button"
            data-session-id={sessionId}
          >
            <History className="size-4" aria-hidden />
            {/* Two tight lines so the button keeps the row's h-8. */}
            <span className="flex flex-col items-start gap-0.5 leading-none">
              <span>Session Timeline</span>
              <span className="text-[11px] font-normal text-muted-foreground">
                {count}
              </span>
            </span>
          </Button>
        </TooltipTrigger>
        <TooltipContent className="max-w-xs">
          {SESSION_TIMELINE_BUTTON_HINT}
        </TooltipContent>
      </Tooltip>
      {open && (
        <SessionTimelineDrawer
          open={open}
          onOpenChange={setOpen}
          organizationId={organizationId}
          occurrenceId={occurrenceId}
        />
      )}
    </>
  );
}
